import { Link, useNavigate, useLocation, Link as RouterLink } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import { ROUTES } from '@/utils/constants'
import { useTranslation } from 'react-i18next'
import { useState } from 'react'
import { Breadcrumbs, Typography, Box, Link as MuiLink } from '@mui/material'
import { NavigateNext, Home } from '@mui/icons-material'
import { SidebarFooter } from '@/components/layout/SidebarFooter'
import { NotificationBell } from '@/components/notifications/NotificationBell'
import { ActiveLessonBar } from '@/components/lessons/ActiveLessonBar'
import '@/styles/dashboard.css'

interface ProfileLayoutProps {
  children: React.ReactNode
}

export function ProfileLayout({ children }: ProfileLayoutProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const user = useAuthStore((state) => state.user)
  const [isMenuOpen, setIsMenuOpen] = useState(false)

  return (
    <div className="dashboard-layout">
      {/* Mobile Header */}
      <header className="dashboard-header-mobile">
        <button
          className="menu-toggle"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          aria-label={t('dashboard.toggleMenu')}
        >
          <i className="fas fa-bars"></i>
        </button>
        <Link to={ROUTES.DASHBOARD} className="dashboard-logo-mobile">
          <img 
            src="/icon/android-chrome-192x192.png" 
            alt="EASYA" 
            onError={(e) => {
              const target = e.target as HTMLImageElement
              target.src = '/icon/apple-touch-icon.png'
            }} 
          />
          <span>EASYA</span>
        </Link>
        <NotificationBell />
        {/* Открытое меню шапка перекрывает сверху: плашка тогда в самом меню */}
        {!isMenuOpen && <ActiveLessonBar placement="header" />}
      </header>

      <div className="dashboard-container">
        {/* Sidebar */}
        <aside className={`dashboard-sidebar ${isMenuOpen ? 'open' : ''}`}>
          {/* Logo */}
          <div className="sidebar-logo">
            <Link to={ROUTES.DASHBOARD} className="dashboard-logo">
              <img 
                src="/icon/android-chrome-192x192.png" 
                alt="EASYA" 
                onError={(e) => {
                  const target = e.target as HTMLImageElement
                  target.src = '/icon/apple-touch-icon.png'
                }} 
              />
              <span>EASYA</span>
            </Link>
            <NotificationBell />
          </div>

          <ActiveLessonBar placement="sidebar" />

          <nav className="dashboard-nav">
            <div className="nav-item nav-item-student-name">
              <button
                type="button"
                className="nav-item-student-back"
                onClick={() => {
                  navigate(ROUTES.DASHBOARD)
                }}
                aria-label={t('profile.back')}
              >
                <i className="fas fa-arrow-left"></i>
              </button>
              <i className="fas fa-user nav-item-student-icon" aria-hidden />
              <span className="nav-item-student-name-text">{t('profile.title')}</span>
            </div>
            {/* Аккаунт */}
            {user?.roles?.some((r) => r.slug === 'teacher') && (
              <div className="nav-section-label">{t('profile.sections.account', 'Аккаунт')}</div>
            )}
            <Link
              to={ROUTES.PROFILE}
              className={`nav-item ${location.pathname === ROUTES.PROFILE ? 'active' : ''}`}
              onClick={() => setIsMenuOpen(false)}
              style={{ paddingLeft: '2rem' }}
            >
              <i className="fas fa-user"></i>
              <span>{t('profile.personalInfo')}</span>
            </Link>
            <Link
              to={ROUTES.PROFILE_NOTIFICATIONS}
              className={`nav-item ${location.pathname === ROUTES.PROFILE_NOTIFICATIONS ? 'active' : ''}`}
              onClick={() => setIsMenuOpen(false)}
              style={{ paddingLeft: '2rem' }}
            >
              <i className="fas fa-bell"></i>
              <span>{t('notifications.preferences.title')}</span>
            </Link>
            <Link
              to={ROUTES.PROFILE_ACTIVITY}
              className={`nav-item ${location.pathname === ROUTES.PROFILE_ACTIVITY ? 'active' : ''}`}
              onClick={() => setIsMenuOpen(false)}
              style={{ paddingLeft: '2rem' }}
            >
              <i className="fas fa-chart-simple"></i>
              <span>{t('activity.title')}</span>
            </Link>
            <Link
              to={ROUTES.PROFILE_SECURITY}
              className={`nav-item ${location.pathname === ROUTES.PROFILE_SECURITY ? 'active' : ''}`}
              onClick={() => setIsMenuOpen(false)}
              style={{ paddingLeft: '2rem' }}
            >
              <i className="fas fa-shield-halved"></i>
              <span>{t('profile.security.title')}</span>
            </Link>
            {user?.roles?.some((r) => r.slug === 'teacher' || r.slug === 'admin') && (
              <Link
                to={ROUTES.PROFILE_CONNECTIONS}
                className={`nav-item ${location.pathname === ROUTES.PROFILE_CONNECTIONS ? 'active' : ''}`}
                onClick={() => setIsMenuOpen(false)}
                style={{ paddingLeft: '2rem' }}
              >
                <i className="fas fa-robot"></i>
                <span>{t('mcpConnections.title')}</span>
              </Link>
            )}
            {user?.roles?.some((r) => r.slug === 'student') && (
              <Link
                to={ROUTES.MY_SCHEDULE}
                className={`nav-item ${location.pathname === ROUTES.MY_SCHEDULE ? 'active' : ''}`}
                onClick={() => setIsMenuOpen(false)}
                style={{ paddingLeft: '2rem' }}
              >
                <i className="fas fa-calendar-alt"></i>
                <span>{t('lessonSchedules.title')}</span>
              </Link>
            )}
            {user?.roles?.some((r) => r.slug === 'teacher') && (
              <Link
                to={ROUTES.PROFILE_SETTINGS}
                className={`nav-item ${location.pathname === ROUTES.PROFILE_SETTINGS ? 'active' : ''}`}
                onClick={() => setIsMenuOpen(false)}
                style={{ paddingLeft: '2rem' }}
              >
                <i className="fas fa-cog"></i>
                <span>{t('profile.settings.title')}</span>
              </Link>
            )}
            {user?.roles?.some((r) => r.slug === 'teacher') && (
              <>
                {/* Мои платежи платформе: кошелёк → подписка → история платежей */}
                <div className="nav-section-label">
                  {t('profile.sections.payments', 'Платежи и подписка')}
                </div>
                <Link
                  to={ROUTES.PROFILE_BALANCE}
                  className={`nav-item ${location.pathname === ROUTES.PROFILE_BALANCE ? 'active' : ''}`}
                  onClick={() => setIsMenuOpen(false)}
                  style={{ paddingLeft: '2rem' }}
                >
                  <i className="fas fa-coins"></i>
                  <span>{t('balance.title', 'Баланс')}</span>
                </Link>
                <Link
                  to={ROUTES.PROFILE_SUBSCRIPTION}
                  className={`nav-item ${location.pathname === ROUTES.PROFILE_SUBSCRIPTION ? 'active' : ''}`}
                  onClick={() => setIsMenuOpen(false)}
                  style={{ paddingLeft: '2rem' }}
                >
                  <i className="fas fa-credit-card"></i>
                  <span>{t('dashboard.subscription')}</span>
                </Link>
                {/* Настройки цен и абонементов для учеников */}
                <div className="nav-section-label">
                  {t('profile.sections.students', 'Для учеников')}
                </div>
                <Link
                  to={ROUTES.PROFILE_BILLING}
                  className={`nav-item ${location.pathname === ROUTES.PROFILE_BILLING ? 'active' : ''}`}
                  onClick={() => setIsMenuOpen(false)}
                  style={{ paddingLeft: '2rem' }}
                >
                  <i className="fas fa-wallet"></i>
                  <span>{t('billing.teacherNavTitle', 'Цены и абонементы')}</span>
                </Link>
                <Link
                  to={ROUTES.PROFILE_STUDENT_FORM_FIELDS}
                  className={`nav-item ${location.pathname === ROUTES.PROFILE_STUDENT_FORM_FIELDS ? 'active' : ''}`}
                  onClick={() => setIsMenuOpen(false)}
                  style={{ paddingLeft: '2rem' }}
                >
                  <i className="fas fa-clipboard-list"></i>
                  <span>{t('studentForms.fieldsTitle')}</span>
                </Link>
              </>
            )}
          </nav>

          {/* Footer with Language Switcher and Profile */}
          <SidebarFooter onMenuClose={() => setIsMenuOpen(false)} />
        </aside>

        {/* Main Content */}
        <main className="dashboard-main">
          <Box sx={{ p: 3, pb: 0 }}>
            <Breadcrumbs
              separator={<NavigateNext fontSize="small" />}
              aria-label="breadcrumb"
              sx={{ mb: 2 }}
            >
              <MuiLink
                component={RouterLink}
                to={ROUTES.DASHBOARD}
                underline="hover"
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                <Home sx={{ mr: 0.5 }} fontSize="inherit" />
                {t('dashboard.home')}
              </MuiLink>
              {location.pathname === ROUTES.PROFILE ? (
                <Typography color="text.primary">
                  {t('dashboard.profile')}
                </Typography>
              ) : (
                <MuiLink component={RouterLink} to={ROUTES.PROFILE} underline="hover">
                  {t('dashboard.profile')}
                </MuiLink>
              )}
              {location.pathname === ROUTES.MY_SCHEDULE && (
                <Typography color="text.primary">
                  {t('lessonSchedules.title')}
                </Typography>
              )}
              {location.pathname === ROUTES.PROFILE_SUBSCRIPTION && (
                <Typography color="text.primary">
                  {t('dashboard.subscription')}
                </Typography>
              )}
              {location.pathname === ROUTES.PROFILE_BILLING && (
                <Typography color="text.primary">
                  {t('billing.teacherPageTitle', 'Тарифы и абонементы')}
                </Typography>
              )}
              {location.pathname === ROUTES.PROFILE_BALANCE && (
                <Typography color="text.primary">
                  {t('balance.title', 'Баланс')}
                </Typography>
              )}
              {location.pathname === ROUTES.PROFILE_SETTINGS && (
                <Typography color="text.primary">
                  {t('profile.settings.title')}
                </Typography>
              )}
              {location.pathname === ROUTES.PROFILE_NOTIFICATIONS && (
                <Typography color="text.primary">
                  {t('notifications.preferences.title')}
                </Typography>
              )}
              {location.pathname === ROUTES.PROFILE_ACTIVITY && (
                <Typography color="text.primary">
                  {t('activity.title')}
                </Typography>
              )}
              {location.pathname === ROUTES.PROFILE_SECURITY && (
                <Typography color="text.primary">
                  {t('profile.security.title')}
                </Typography>
              )}
              {location.pathname === ROUTES.PROFILE_CONNECTIONS && (
                <Typography color="text.primary">
                  {t('mcpConnections.title')}
                </Typography>
              )}
              {location.pathname === ROUTES.PROFILE_STUDENT_FORM_FIELDS && (
                <Typography color="text.primary">{t('studentForms.fieldsTitle')}</Typography>
              )}
              {location.pathname === ROUTES.PROFILE_PLANS && (
                <MuiLink component={RouterLink} to={ROUTES.PROFILE_SUBSCRIPTION} underline="hover">
                  {t('dashboard.subscription')}
                </MuiLink>
              )}
              {location.pathname === ROUTES.PROFILE_PLANS && (
                <Typography color="text.primary">
                  {t('subscription.changePlan')}
                </Typography>
              )}
            </Breadcrumbs>
          </Box>
          {children}
        </main>
      </div>

      {/* Overlay for mobile */}
      {isMenuOpen && (
        <div
          className="dashboard-overlay"
          onClick={() => setIsMenuOpen(false)}
        />
      )}
    </div>
  )
}
